import React from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import logo from "../../../assets/fitoja-logo.jpg";
import maleDefaultPic from "../../../assets/male-profile-picture.png";
import femaleDefaultPic from "../../../assets/female-profile-picture.png";
import closeIcon from "../../../assets/close_icon.png";
import pureVegIcon from "../../../assets/pure-veg-symbol.jpg";
import nonVegIcon from "../../../assets/non-veg-symbol.jpg";
import "./manage_diet.scss";
import parse from "html-react-parser";

interface UserDetails {
  name: string;
  email: string;
  phoneNumber: string;
  role: string;
  gender: string;
}

interface DietPlan {
  _id: string;
  mealType: string;
  diet: string;
}

interface UserDietPlan {
  _id: string;
  phoneNumber: string;
  name: string;
  dietPlanSent: boolean;
  dietPlanStartDate: string | null;
  dietPlan: DietPlan[];
  totalDiets: number;
  totalDietsSent: number;
  gender: string;
  age: string;
  weight: string;
  height: string;
  goal: string;
  activity: string;
  medicalConcern: string;
  conditions: object;
  dietaryType: string;
  mealPreference: any;
  goalWeightLoss: string;
  goalWeightGain: string;
  goalMuscleGain: string;
  goalHealthIssues: string;
  oneSignalPlayerId: string;
  profilePicture: string;
  languageCode: string;
}

interface DietCategory {
  _id: string;
  dietCategory: string;
  dietPlan: DietPlan[];
}

const ManageDiet: React.FC = () => {
  const [role, setRole] = useState<string>("");
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDietPlanSent, setShowDietPlanSent] = useState<boolean>(false);
  const [showDietCompleted, setShowDietCompleted] = useState<boolean>(false);
  const [allUsersDietPlans, setAllUsersDietPlans] = useState<UserDietPlan[]>(
    []
  );
  const [dietCategories, setDietCategories] = useState<DietCategory[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDietPlan | null>(null);
  const [selectedDietCategory, setSelectedDietCategory] = useState<string>("");
  const [selectedDietPlans, setSelectedDietPlans] = useState<DietPlan[]>([]);
  const [dietPlanStartDate, setDietPlanStartDate] = useState<Date | null>(null);
  const [showSendPopup, setShowSendPopup] = useState<boolean>(false);
  const [showChangePopup, setShowChangePopup] = useState<boolean>(false);
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchNotSentTerm, setSearchNotSentTerm] = useState<string>("");
  const [searchSentTerm, setSearchSentTerm] = useState<string>("");
  const [searchCompletedTerm, setSearchCompletedTerm] = useState<string>(""); // New states for sub-tabs

  const [showNewClients, setShowNewClients] = useState<boolean>(true);
  const [showOldClients, setShowOldClients] = useState<boolean>(false);

  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");

      if (!email || !token) {
        navigate("/login");
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );

      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllUsersDietPlan();
      fetchAllDietCategories();
    } catch (error) {
      handleLogout();
    }
  };

  const fetchAllUsersDietPlan = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-users-info`
      );
      setAllUsersDietPlans(response.data.data);
    } catch (error) {
      console.error("Failed to fetch users with their diet plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDietCategories = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-diet-plans`
      );
      setDietCategories(response.data.data);
    } catch (error) {
      console.error("Failed to fetch diet categories:", error);
    }
  };

  const handleSendDietPlanClick = (user: UserDietPlan) => {
    setSelectedUser(user);
    setSelectedDietCategory("");
    setSelectedDietPlans([]);
    setDietPlanStartDate(null);
    setShowSendPopup(true);
  };

  const handleDietCategoryChange = (categoryId: string) => {
    setSelectedDietCategory(categoryId);

    const selectedCategory = dietCategories.find(
      (category) => category._id === categoryId
    );
    if (selectedCategory) {
      setSelectedDietPlans(selectedCategory.dietPlan);
    }
  };

  const handleSendDietPlan = async () => {
    console.log(
      "selectedUser.oneSignalPlayerId: ",
      selectedUser?.oneSignalPlayerId
    );
    if (!selectedDietCategory || !selectedUser || !dietPlanStartDate) {
      return;
    }

    const selectedCategory = dietCategories.find(
      (category) => category._id === selectedDietCategory
    );

    if (!selectedCategory) {
      console.error("Selected diet category not found.");
      return;
    }

    const istDate = new Date(dietPlanStartDate.getTime() + 5.5 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

    try {
      await axios.post(`https://api.fitoja.in/api/save-personal-info`, {
        phoneNumber: selectedUser.phoneNumber,
        dietPlanSent: true,
        dietPlanStartDate: istDate,
        dietPlan: selectedCategory.dietPlan,
        totalDietsSent: selectedUser.totalDietsSent + 1,
        totalDiets: selectedUser.totalDiets,
      });
      sendNotification(
        selectedUser.phoneNumber,
        "Your Personalized Diet Plan is Ready!",
        "Great news! Your customized diet plan has been prepared by our experts. Check it now in the diet section and start your journey towards a healthier you!"
      );
      if (selectedUser.oneSignalPlayerId != null) {
        sendPushNotification(selectedUser.oneSignalPlayerId);
      }
      setShowSendPopup(false);
      fetchAllUsersDietPlan();
    } catch (error) {
      console.error("Failed to send diet plan:", error);
    }
  };

  const handleEditDietPlan = async () => {
    console.log("yuwveh");
    if (!selectedDietCategory || !selectedUser) {
      return;
    }

    const selectedCategory = dietCategories.find(
      (category) => category._id === selectedDietCategory
    );

    if (!selectedCategory) {
      console.error("Selected diet category not found.");
      return;
    }

    try {
      await axios.post(`https://api.fitoja.in/api/save-personal-info`, {
        phoneNumber: selectedUser.phoneNumber,
        dietPlan: selectedCategory.dietPlan,
      });
      sendNotification(
        selectedUser.phoneNumber,
        "As requested, your Personalized Diet Plan has been successfully changed!",
        "Great news! As requested, our experts have prepared your new customized diet plan. Check it now in the Diet section and begin your journey to a healthier you!"
      );
      if (selectedUser.oneSignalPlayerId != null) {
        sendPushNotification(selectedUser.oneSignalPlayerId);
      }
      setShowChangePopup(false);
      fetchAllUsersDietPlan();
    } catch (error) {
      console.error("Failed to send diet plan:", error);
    }
  };

  const handleEditDietPlanClick = (user: UserDietPlan) => {
    const existingCategory = dietCategories.find((category) =>
      category.dietPlan.some((plan) =>
        user.dietPlan.some((userPlan) => userPlan._id === plan._id)
      )
    );

    setSelectedUser(user);
    setSelectedDietCategory(existingCategory?._id || "");
    setSelectedDietPlans(user.dietPlan || []);
    setShowChangePopup(true);
  };

  const sendNotification = async (
    notificationPhoneNumber,
    notificationTitle,
    notificationMessage
  ) => {
    try {
      const data = {
        phoneNumber: notificationPhoneNumber,
        title: notificationTitle,
        description: notificationMessage,
        isViewed: false,
        isSubscriptionAlert: false,
      };

      const response = await axios.post(
        "https://api.fitoja.in/api/create-notification",
        data
      );
    } catch (error) {
      console.error("Failed to send notification:", error);
    }
  };

  const sendPushNotification = async (playerId) => {
    const response = await axios.post(
      "https://onesignal.com/api/v1/notifications",
      {
        app_id: "18b90dfb-da22-4dbe-b507-95930f99fe9a",
        include_player_ids: [playerId],
        headings: { en: "📢 FITOJA Update: Your Diet Plan is Ready! 🍽️" },
        contents: {
          en: "Prepared by experts, just for you! Check it now in the FITOJA app. 🚀",
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization:
            "Basic os_v2_app_dc4q3662ejg35nihswjq7gp6tiwo7kz44ateraul4cl7hqkha6mtkhjcfl4srw7pk3imzs5cssaqqkxzrxjvh5xi3n65pq6ln4fqeca",
        },
      }
    );

    console.log(response.data);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    navigate("/login");
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  const isDietCompleted = (user: UserDietPlan): boolean => {
    return user.totalDietsSent === user.totalDiets; // Diet is completed if total diets sent equals total diets
  };

  const languageMap = {
    en: "English",
    hi: "Hindi",
    pa: "Punjabi",
    mr: "Marathi",
    ta: "Tamil",
    te: "Telugu",
    ur: "Urdu",
    or: "Odia",
    bn: "Bengali",
    gu: "Gujarati",
    kn: "Kannada",
    ml: "Malayalam",
    as: "Assamese",
  };

  return (
    <div className="manage-diet-container">
      {" "}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div> <p>Loading, please wait...</p>{" "}
        </div>
      )}{" "}
      {!loading && (
        <>
          {" "}
          <header className="manage-diet-header">
            {" "}
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />{" "}
            </div>{" "}
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              {" "}
              <img
                className="profile-pic"
                src={
                  userDetails?.gender == "Male"
                    ? maleDefaultPic
                    : femaleDefaultPic
                }
                alt="Profile"
              />{" "}
            </div>{" "}
          </header>{" "}
          {showOverlay && userDetails && (
            <div className="profile-overlay">
              {" "}
              <div className="overlay-content">
                {" "}
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />{" "}
                <p>
                  <strong>Name:</strong> {userDetails.name}{" "}
                </p>{" "}
                <p>
                  <strong>Email:</strong> {userDetails.email}{" "}
                </p>{" "}
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}{" "}
                </p>{" "}
                <p>
                  <strong>Role:</strong> {userDetails.role}{" "}
                </p>{" "}
                <p>
                  <strong>Gender:</strong> {userDetails.gender}{" "}
                </p>{" "}
                <button className="logout-button" onClick={handleLogout}>
                  Logout{" "}
                </button>{" "}
              </div>{" "}
            </div>
          )}
          <br></br> <br></br>
          <br></br> <br></br> <h1>Manage Diet Plans</h1>{" "}
          <div className="toggle-container">
            {" "}
            <div
              className={`toggle-option ${
                !showDietPlanSent && !showDietCompleted ? "active" : ""
              }`}
              // Updated onClick to handle sub-tabs
              onClick={() => {
                setShowDietPlanSent(false);
                setShowDietCompleted(false);
                setShowNewClients(true); // Default to New Clients sub-tab
                setShowOldClients(false);
              }}
            >
              Diet Plan yet to be Sent{" "}
            </div>{" "}
            <div
              className={`toggle-option ${
                showDietPlanSent && !showDietCompleted ? "active" : ""
              }`}
              onClick={() => {
                setShowDietPlanSent(true);
                setShowDietCompleted(false);
                setShowNewClients(false);
                setShowOldClients(false);
              }}
            >
              Diet Plan Sent{" "}
            </div>{" "}
            <div
              className={`toggle-option ${showDietCompleted ? "active" : ""}`}
              onClick={() => {
                setShowDietPlanSent(false);
                setShowDietCompleted(true);
                setShowNewClients(false);
                setShowOldClients(false);
              }}
            >
              All Diets Completed{" "}
            </div>{" "}
          </div>{" "}
          {!showDietPlanSent && !showDietCompleted && (
            <div className="sub-toggle-container">
              <div
                className={`toggle-option ${showNewClients ? "active" : ""}`}
                onClick={() => {
                  setShowNewClients(true);
                  setShowOldClients(false);
                }}
              >
                New Clients
              </div>
              <div
                className={`toggle-option ${showOldClients ? "active" : ""}`}
                onClick={() => {
                  setShowNewClients(false);
                  setShowOldClients(true);
                }}
              >
                Old Clients
              </div>{" "}
            </div>
          )}{" "}
          {!showDietPlanSent && !showDietCompleted && (
            <input
              className="search-box tab-search-box"
              type="text"
              placeholder="Search user by Phone Number"
              value={searchNotSentTerm}
              onChange={(e) => setSearchNotSentTerm(e.target.value)}
            />
          )}{" "}
          {showDietPlanSent && !showDietCompleted && (
            <input
              className="search-box tab-search-box"
              type="text"
              placeholder="Search user by Phone Number"
              value={searchSentTerm}
              onChange={(e) => setSearchSentTerm(e.target.value)}
            />
          )}{" "}
          {showDietCompleted && (
            <input
              className="search-box tab-search-box"
              type="text"
              placeholder="Search user by Phone Number"
              value={searchCompletedTerm}
              onChange={(e) => setSearchCompletedTerm(e.target.value)}
            />
          )}{" "}
          <div className="user-list">
            {" "}
            {allUsersDietPlans.length > 0 ? (
              allUsersDietPlans
                .filter((user) => {
                  if (!showDietPlanSent && !showDietCompleted) {
                    if (showNewClients) {
                      return (
                        user.totalDietsSent === 0 &&
                        !user.dietPlanSent &&
                        user.phoneNumber.includes(searchNotSentTerm)
                      );
                    } else if (showOldClients) {
                      return (
                        (user.totalDietsSent > 0 && user.totalDietsSent != user.totalDiets) &&
                        !user.dietPlanSent &&
                        user.phoneNumber.includes(searchNotSentTerm)
                      );
                    }
                  } else if (showDietPlanSent && !showDietCompleted) {
                    return (
                      user.dietPlanSent === showDietPlanSent &&
                      user.phoneNumber.includes(searchSentTerm)
                    );
                  } else if (showDietCompleted) {
                    return (
                      isDietCompleted(user) &&
                      user.phoneNumber.includes(searchCompletedTerm) &&
                      user.totalDiets !== null &&
                      user.dietPlanStartDate &&
                      new Date(user.dietPlanStartDate) <
                        new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
                    );
                  }
                  return false;
                })
                .map((user) => (
                  <div key={user._id} className="user-card">
                    {" "}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "baseline",
                      }}
                    >
                      {" "}
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Phone:</strong> {user.phoneNumber}{" "}
                      </span>{" "}
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Name:</strong> {user.name}{" "}
                      </span>{" "}
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Total Diets:</strong> {user.totalDietsSent} /{" "}
                        {user.totalDiets}{" "}
                      </span>{" "}
                      <span
                        style={{
                          display: "flex",
                          gap: "10px",
                          fontSize: "20px",
                          fontWeight: "600",
                        }}
                      >
                        <strong>Goal:</strong>{" "}
                        <span style={{ display: "flex", gap: "10px" }}>
                          {user.goalWeightLoss == "true" ? (
                            <span
                              style={{
                                color: "Green",
                                background: "white",
                                padding: "0px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              "Weight Loss"
                            </span>
                          ) : (
                            ""
                          )}
                          {user.goalWeightGain == "true" ? (
                            <span
                              style={{
                                color: "Green",
                                background: "white",
                                padding: "0px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              "Weight Gain"
                            </span>
                          ) : (
                            ""
                          )}
                          {user.goalMuscleGain == "true" ? (
                            <span
                              style={{
                                color: "Green",
                                background: "white",
                                padding: "0px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              "Muscle Gain"
                            </span>
                          ) : (
                            ""
                          )}
                          {user.goalHealthIssues == "true" ? (
                            <span
                              style={{
                                color: "Green",
                                background: "white",
                                padding: "0px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              "Other Health issues"
                            </span>
                          ) : (
                            ""
                          )}
                        </span>
                      </span>{" "}
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Gender:</strong> {user.gender}{" "}
                      </span>{" "}
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Age:</strong> {user.age}{" "}
                      </span>{" "}
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Weight:</strong> {user.weight}{" "}
                      </span>{" "}
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Height:</strong> {user.height}{" "}
                      </span>{" "}
                      <div>
                        {user.mealPreference.length > 0 ? (
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: "600",
                              display: "flex",
                              gap: "10px",
                            }}
                          >
                            <strong>Meal Preferences:</strong>
                            <span
                              style={{
                                color: "blue",
                                background: "white",
                                padding: "0px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              {user.mealPreference.join(", ")}
                            </span>
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: "600",
                              display: "flex",
                              gap: "10px",
                            }}
                          >
                            <strong>Meal Preferences:</strong>
                            <span style={{ color: "yellow" }}>
                              No Preferred Meal
                            </span>
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Language:</strong>{" "}
                        {languageMap[user.languageCode] || "Unknown Language"}{" "}
                      </span>
                      <span style={{ fontSize: "20px", fontWeight: "600" }}>
                        <strong>Activity:</strong> {user.activity}{" "}
                      </span>{" "}
                      {user.medicalConcern == "Yes" ? (
                        <span
                          style={{
                            fontSize: "20px",
                            fontWeight: "600",
                            display: "flex",
                            gap: "10px",
                          }}
                        >
                          <strong>Diseases: </strong>
                          <div
                            style={{
                              color: "red",
                              background: "white",
                              padding: "0px 10px",
                              borderRadius: "4px",
                            }}
                          >
                            {Object.entries(user.conditions)
                              .filter(([key, value]) => value === true)
                              .map(([key]) => {
                                const diseaseNames = {
                                  diabetes: "Diabetes",
                                  menopause: "Menopause",
                                  pcosPcod: "PCOS/PCOD",
                                  cardiovascular: "Cardiovascular",
                                  immunity: "Immunity",
                                  hairAndSkin: "Hair & Skin",
                                  highUricAcid: "High Uric Acid",
                                  anemia: "Anemia",
                                  ulcerativeColitis: "Ulcerative Colitis",
                                  crohnsDisease: "Crohn's Disease",
                                  sle: "SLE",
                                  psoriasis: "Psoriasis",
                                  hashimotos: "Hashimoto's",
                                  irritableBowelSyndrome:
                                    "Irritable Bowel Syndrome",
                                  thyroidism: "Thyroidism",
                                  antiCancerDiets: "Anti-Cancer Diets",
                                  otherMedicalConditions:
                                    user.conditions[
                                      "otherMedicalConditionsDes"
                                    ],
                                };

                                return diseaseNames[key] || null;
                              })
                              .filter(Boolean)
                              .join(" | ")}
                          </div>
                        </span>
                      ) : (
                        <span style={{ fontSize: "20px", fontWeight: "600" }}>
                          <strong>Diseases:</strong>{" "}
                          <span style={{ color: "greenyellow" }}>
                            No disease
                          </span>
                        </span>
                      )}
                    </div>{" "}
                    <div
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {user.dietaryType == "Vegetarian" ? (
                        <div className="veg-container">
                          <img src={pureVegIcon} height="20px"></img>
                          <span>Vegetarian</span>
                        </div>
                      ) : (
                        <div className="nonveg-container">
                          <img src={nonVegIcon} height="20px"></img>
                          <span>Non-Vegetarian</span>
                        </div>
                      )}
                      {user?.profilePicture != null ? (
                        <img
                          className="user-card-profile-pic"
                          src={user?.profilePicture}
                        ></img>
                      ) : user.gender == "Male" ? (
                        <img
                          className="user-card-profile-pic"
                          src={maleDefaultPic}
                        ></img>
                      ) : (
                        <img
                          className="user-card-profile-pic"
                          src={femaleDefaultPic}
                        ></img>
                      )}
                      {user.dietPlanSent ? (
                        <>
                          {" "}
                          <button
                            className="edit-diet-btn"
                            onClick={() => handleEditDietPlanClick(user)}
                          >
                            Change Diet Plan{" "}
                          </button>{" "}
                        </>
                      ) : (
                        <>
                          {" "}
                          <button
                            className="send-diet-btn"
                            onClick={() => handleSendDietPlanClick(user)}
                          >
                            Send Diet Plan{" "}
                          </button>{" "}
                        </>
                      )}{" "}
                    </div>
                  </div>
                ))
            ) : (
              <div className="no-users">No users registered.</div>
            )}{" "}
          </div>{" "}
        </>
      )}{" "}
      {showSendPopup && selectedUser && (
        <div className="popup">
          {" "}
          <div className="popup-content">
            {" "}
            <img
              className="close-icon"
              src={closeIcon}
              onClick={() => setShowSendPopup(false)}
              height={"30px"}
            />
            <h3>Send Diet Plan to {selectedUser.name}</h3>{" "}
            <select
              value={selectedDietCategory}
              onChange={(e) => handleDietCategoryChange(e.target.value)}
            >
              <option value="">Choose a category</option>{" "}
              {dietCategories.map((category) => (
                <option value={category._id} key={category._id}>
                  {category.dietCategory}{" "}
                </option>
              ))}{" "}
            </select>
            <br />
            <br />{" "}
            {selectedDietPlans.length > 0 && (
              <div className="diet-plan-list">
                {" "}
                <div className="manage-diet-plan-item">
                  <h3>Diet Plan:</h3>{" "}
                  <ul>
                    {" "}
                    {selectedDietPlans.map((plan) => (
                      <li key={plan._id}>
                        {" "}
                        <strong className="meal-type">
                          {parse(plan.mealType)}:{" "}
                        </strong>{" "}
                        {parse(plan.diet)}{" "}
                      </li>
                    ))}{" "}
                  </ul>{" "}
                </div>{" "}
              </div>
            )}{" "}
            <div style={{ display: "grid", gap: "10px", marginTop: "10px" }}>
              {" "}
              <label htmlFor="startDate">Start Date:</label>{" "}
              <DatePicker
                selected={dietPlanStartDate}
                onChange={(date) => setDietPlanStartDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select a start date"
                minDate={new Date()}
                value={
                  dietPlanStartDate
                    ? new Date(
                        dietPlanStartDate.getTime() + 5.5 * 60 * 60 * 1000
                      ).toLocaleDateString("en-IN")
                    : ""
                }
              />{" "}
              <button
                className="send-diet-plan-btn"
                onClick={handleSendDietPlan}
                disabled={!selectedDietCategory || !dietPlanStartDate}
              >
                Send Diet Plan{" "}
              </button>{" "}
            </div>{" "}
          </div>{" "}
        </div>
      )}{" "}
      {showChangePopup && selectedUser && (
        <div className="popup">
          {" "}
          <div className="popup-content">
            {" "}
            <img
              className="close-icon"
              src={closeIcon}
              onClick={() => setShowChangePopup(false)}
              height={"30px"}
            />
            <h3>Change {selectedUser.name}'s Diet Plan</h3>{" "}
            <select
              value={selectedDietCategory}
              onChange={(e) => handleDietCategoryChange(e.target.value)}
            >
              <option value="">Choose a category</option>{" "}
              {dietCategories.map((category) => (
                <option value={category._id} key={category._id}>
                  {category.dietCategory}{" "}
                </option>
              ))}{" "}
            </select>
            <br />
            <br />{" "}
            {selectedDietPlans.length > 0 && (
              <div className="diet-plan-list">
                {" "}
                <div className="manage-diet-plan-item">
                  <h3>Diet Plan:</h3>{" "}
                  <ul>
                    {" "}
                    {selectedDietPlans.map((plan) => (
                      <li key={plan._id}>
                        {" "}
                        <strong className="meal-type">
                          {parse(plan.mealType)}:{" "}
                        </strong>{" "}
                        {parse(plan.diet)}{" "}
                      </li>
                    ))}{" "}
                  </ul>{" "}
                </div>{" "}
              </div>
            )}{" "}
            <div style={{ display: "grid", gap: "10px", marginTop: "10px" }}>
              <button
                className="send-diet-plan-btn"
                onClick={handleEditDietPlan}
                disabled={!selectedDietCategory}
              >
                Change Diet Plan{" "}
              </button>{" "}
            </div>{" "}
          </div>{" "}
        </div>
      )}{" "}
    </div>
  );
};

export default ManageDiet;
